import React, { useRef } from "react";
import {
  DataGrid,
  GridColDef,
  GridEventListener,
  GridRowsProp,
} from "@mui/x-data-grid";
import styled from "styled-components";
import { Pagination, PaginationItem, Stack } from "@mui/material";
import { ArrowForwardWhite, ArrowBackWhite } from "../../shared/icons";

// Styled Components for Styling
const TableWrapper = styled.div`
  margin-bottom: 20px;
  overflow-x: auto;
  padding-bottom: 20px;
`;

const DataGridWrapper = styled.div`
  min-width: 800px;
  & .MuiDataGrid-cell:focus {
    outline: none;
  }

  & .MuiDataGrid-row {
    cursor: pointer;

    .MuiDataGrid-cell {
      padding-left: 18px;
      padding-right: 18px;
    }
  }

  & .MuiDataGrid-columnHeader {
    padding-left: 18px;
    padding-right: 18px;
  }

  & .MuiDataGrid-container--top [role=row] {
    background-color: transparent !important;
  }

  '& .super-app-theme--header': {
     backgroundColor: 'rgba(255, 7, 0, 0.55) !important',
  },
`;

const StyledButton = styled.button`
  background-color: #555;
  color: white;
  font-size: 16px;
  font-weight: 600;
  padding: 10px 20px;
  border: none;
  border-radius: 80px;
  display: flex;
  align-items: center;
  cursor: pointer;
  transition: background-color 0.3s ease;
  &:hover {
    background-color: #555;
  }
  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }
`;

const IconWrapper = styled.span`
  margin-inline: 8px;
  display: flex;
  align-items: center;
`;
const arrowRightFun = () => {
  return (
    <StyledButton>
      Next
      <IconWrapper>
        <ArrowForwardWhite width={16} height={16} />
      </IconWrapper>
    </StyledButton>
  );
};

const arrowLeftFun = () => {
  return (
    <StyledButton>
      <IconWrapper>
        <ArrowBackWhite width={16} height={16} />
      </IconWrapper>
      Previous
    </StyledButton>
  );
};

interface CommonProps {
  rows: GridRowsProp;
  columns: GridColDef[];
  handleRowClick?: GridEventListener<"rowClick">;
}

interface PaginationProps {
  pagnation: true;
  totalPages: number;
  currentPage: number;
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
}

interface NonPaginationProps {
  pagnation?: false;
  totalPages?: never;
  currentPage?: never;
  setCurrentPage?: never;
  getRecords?: never;
}

type DataGridTableProps = CommonProps & (PaginationProps | NonPaginationProps);

const DataGridTable: React.FC<DataGridTableProps> = ({
  rows,
  columns,
  pagnation,
  totalPages,
  currentPage,
  setCurrentPage,
  handleRowClick,
}) => {
  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    if (setCurrentPage) {
      setCurrentPage(value);
      scrollOnPageChange();
    }
  };
  
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const scrollOnPageChange = () => {
    if (scrollRef.current) {
      const targetPosition = scrollRef.current.offsetTop - 250;
      window.scrollTo({
        top: targetPosition,
        behavior: "smooth",
      });
    }
  }

  return (
    <TableWrapper 
    ref={scrollRef}
    >
      <DataGridWrapper>
        <DataGrid
          sx={{
            fontFamily: "'Manrope', sans-serif !important",
            border: 0,
            "& .MuiDataGrid-container--top [role=row]": {
              backgroundColor: "transparent",
              color: "rgba(22, 22, 26, 0.6)",
              fontWeight: 700,
              fontSize: "12px",
            },
          }}
          rows={rows}
          columns={columns}
          onRowClick={handleRowClick}
          sortingOrder={["asc", "desc"]}
          //   disableClickEventBubbling
          initialState={{
            sorting: {
              sortModel: [{ field: "id", sort: "asc" }],
            },
            pagination: {
              paginationModel: {
                pageSize: 10,
              },
            },
          }}
          rowHeight={59.5}
          pageSizeOptions={[10]}
          disableRowSelectionOnClick
          disableColumnMenu
          hideFooterPagination
          hideFooter
        />
        {pagnation && (
          <Stack
            sx={{ display: "flex", alignItems: "center", mt: 3 }}
            spacing={2}
          >
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={handlePageChange}
              renderItem={(item: any) => (
                <PaginationItem
                  slots={{ previous: arrowLeftFun, next: arrowRightFun }}
                  {...item}
                />
              )}
            />
          </Stack>
        )}
      </DataGridWrapper>
    </TableWrapper>
  );
};

export default DataGridTable;
